import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { documentsApi } from '../../../lib/api/documentsApi.connect';
/**
 * Fetches the signed ONLYOFFICE editor config JSON + the public document
 * server URL. The config contains a short-lived JWT, so it's refreshed on
 * every mount — do not cache aggressively.
 *
 * When shanks applies AI edits to THIS document server-side, it dispatches
 * `shanks-document-applied`; we refetch so the config's new content-versioned
 * document.key reloads the editor in place. Centralising it here means every
 * embedding surface (the chat drawer, Artifact Studio, the proposal Volume
 * editor, the Editor page) picks up applied changes — not just the chat drawer
 * — and the still-open editor can no longer autosave stale bytes over them.
 */ export function useEditSession(documentId, actionLinkBookmark = '') {
    const queryClient = useQueryClient();
    useEffect(()=>{
        if (!documentId) return;
        const onApplied = (event)=>{
            const detail = event.detail;
            // Scoped by documentId so a concurrently-open editor for a different
            // document isn't reloaded.
            if (detail?.documentId === documentId) {
                void queryClient.invalidateQueries({
                    queryKey: [
                        'editor-session',
                        documentId
                    ]
                });
            }
        };
        window.addEventListener('shanks-document-applied', onApplied);
        return ()=>window.removeEventListener('shanks-document-applied', onApplied);
    }, [
        documentId,
        queryClient
    ]);
    return useQuery({
        // The bookmark is part of the signed config, so it is part of the key (the
        // applied-edit invalidation above still matches by prefix).
        queryKey: [
            'editor-session',
            documentId,
            actionLinkBookmark
        ],
        queryFn: ()=>documentId ? documentsApi.getEditSession(documentId, actionLinkBookmark) : Promise.reject(new Error('no document id')),
        enabled: !!documentId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false
    });
}
